import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getXeroPayslips, GetXeroPayslipsOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ChevronRight, ChevronDown, Printer, Eye, PoundSterling } from 'lucide-react';
import { format, parseISO } from 'date-fns';

type Payslip = GetXeroPayslipsOutputType['payslips'][0];
type Detail = GetXeroPayslipsOutputType['detail'];

function fmtDate(d?: string) { if (!d) return '—'; try { return format(parseISO(d), 'dd MMM'); } catch { return d; } }
function fmtCurrency(n: number) { return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function MyPayslips() {
  const { user } = useAuth();
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | undefined>();
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!user) return;
    getXeroPayslips({}).then(d => {
      setPayslips(d.payslips);
      if (d.error) setError(d.error);
    }).catch(() => setError('Failed to load')).finally(() => setLoading(false));
  }, [user]);

  const toggleExpand = async (ps: Payslip) => {
    if (expandedId === ps.payslipId) { setExpandedId(null); setDetail(undefined); return; }
    setExpandedId(ps.payslipId);
    setLoadingDetail(true);
    setDetail(undefined);
    try {
      const res = await getXeroPayslips({ payslipId: ps.payslipId });
      setDetail(res.detail);
    } catch { setDetail(undefined); }
    setLoadingDetail(false);
  };

  const handlePrint = () => window.print();

  if (loading) return (
    <div className="container mx-auto px-4 py-6 max-w-3xl space-y-4">
      <Skeleton className="h-10 w-48" /><Skeleton className="h-60" />
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl space-y-5">
      <div className="flex items-center gap-3">
        <PoundSterling className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-xl font-bold">My Payslips</h1>
          <p className="text-sm text-muted-foreground">Finalised payslips from Xero Payroll</p>
        </div>
      </div>

      <div className="bg-accent/30 border border-border rounded-lg p-3 flex items-start gap-2">
        <Eye className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground">This page shows sensitive pay information. If you're on a shared device, close this tab when finished.</p>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-destructive shrink-0" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {payslips.length === 0 && !error ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No payslips available</CardContent></Card>
      ) : (
        <div className="border border-border rounded-lg divide-y divide-border">
          {/* Header */}
          <div className="hidden sm:flex items-center p-3 bg-muted/30">
            <span className="text-xs font-semibold text-muted-foreground w-32">Period</span>
            <span className="text-xs font-semibold text-muted-foreground w-24">Paid</span>
            <span className="text-xs font-semibold text-muted-foreground w-24 text-right">Gross</span>
            <span className="text-xs font-semibold text-muted-foreground w-24 text-right">Deductions</span>
            <span className="text-xs font-semibold text-muted-foreground w-24 text-right">Net</span>
            <span className="w-8" />
          </div>
          {payslips.map(ps => (
            <div key={ps.payslipId}>
              <button onClick={() => toggleExpand(ps)} className="w-full flex items-center p-3 hover:bg-muted/20 transition-colors text-left">
                <span className="text-sm font-medium w-32">{ps.periodLabel}</span>
                <span className="text-sm text-muted-foreground w-24 hidden sm:block">{fmtDate(ps.paymentDate)}</span>
                <span className="text-sm w-24 text-right hidden sm:block">{fmtCurrency(ps.grossPay)}</span>
                <span className="text-sm text-muted-foreground w-24 text-right hidden sm:block">{fmtCurrency(ps.deductions)}</span>
                <span className="text-sm font-medium w-24 text-right">{fmtCurrency(ps.netPay)}</span>
                <span className="w-8 flex justify-end">
                  {expandedId === ps.payslipId ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                </span>
              </button>
              {expandedId === ps.payslipId && (
                <div className="px-4 pb-4 bg-muted/10">
                  {loadingDetail ? (
                    <div className="space-y-2 py-3"><Skeleton className="h-4 w-48" /><Skeleton className="h-4 w-32" /><Skeleton className="h-4 w-40" /></div>
                  ) : detail ? (
                    <div className="space-y-4 py-3 print:space-y-2" id="payslip-detail">
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Earnings</p>
                        {detail.earningsLines.map((e, i) => (
                          <div key={i} className="flex justify-between text-sm py-1">
                            <span>{e.name}{e.hours ? ` (${e.hours}h)` : ''}</span>
                            <span>{fmtCurrency(e.amount)}</span>
                          </div>
                        ))}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Deductions</p>
                        {detail.deductionLines.map((d, i) => (
                          <div key={i} className="flex justify-between text-sm py-1">
                            <span>{d.name}</span><span>{fmtCurrency(d.amount)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between text-sm py-1"><span>Income Tax</span><span>{fmtCurrency(detail.taxLine)}</span></div>
                        <div className="flex justify-between text-sm py-1"><span>National Insurance</span><span>{fmtCurrency(detail.niLine)}</span></div>
                        {detail.pensionLine > 0 && <div className="flex justify-between text-sm py-1"><span>Pension</span><span>{fmtCurrency(detail.pensionLine)}</span></div>}
                      </div>
                      <div className="border-t border-border pt-2 flex justify-between font-semibold">
                        <span>Net Pay</span><span>{fmtCurrency(detail.netPay)}</span>
                      </div>
                      <Button variant="outline" size="sm" onClick={handlePrint} className="print:hidden">
                        <Printer className="w-4 h-4 mr-1.5" />Print
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-3">Unable to load detail</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
